import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useUpdateTeamShare, type TeamShare } from '@/api/teams';
import { PermissionCheckboxes } from './PermissionCheckboxes';

interface EditPermissionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string;
  /** The share being edited (null when closed). */
  share: TeamShare | null;
}

export function EditPermissionDialog({
  open,
  onOpenChange,
  teamId,
  share,
}: EditPermissionDialogProps) {
  const { show } = useToast();
  const [actions, setActions] = useState<string[]>([]);

  useEffect(() => {
    if (share) setActions(share.actions);
  }, [share]);

  const update = useUpdateTeamShare(teamId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Permission updated' });
      onOpenChange(false);
    },
    onError: () => show({ tone: 'danger', title: 'Could not update permission' }),
  });

  const serviceName = share?.service_name ?? share?.service_id ?? 'this service';
  const canSubmit = !!share && actions.length > 0 && !update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Edit permission</DialogTitle>
          <DialogDescription>Change what this team can do with “{serviceName}”.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Field label="Permissions" required>
            {() => <PermissionCheckboxes value={actions} onChange={setActions} />}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={() => share && update.mutate({ shareId: share.id, data: { actions } })}
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
